import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Globe, 
  Users, 
  Eye, 
  Monitor, 
  Smartphone, 
  Tablet,
  TrendingUp,
  MapPin,
  Link as LinkIcon,
  Calendar,
  RefreshCw
} from 'lucide-react';

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics`, {
        headers: {
          'Authorization': 'Basic ' + btoa('admin:Android00!!!')
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized - check credentials');
        }
        throw new Error('Failed to fetch analytics');
      }
      
      const data = await response.json();
      setAnalytics(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // Auto refresh every 30 seconds
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  const StatCard = ({ icon: Icon, label, value, sublabel }) => (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-5 h-5 text-red-400" />
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <div className="text-3xl font-bold text-white">{value}</div>
      {sublabel && <div className="text-xs text-gray-500 mt-1">{sublabel}</div>}
    </div>
  );

  const DataTable = ({ title, icon: Icon, data, columns }) => (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-red-400" />
        <h3 className="font-semibold text-white">{title}</h3>
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {data && data.length > 0 ? (
          data.map(([name, count], i) => (
            <div key={i} className="flex justify-between items-center py-2 border-b border-white/5">
              <span className="text-gray-300 text-sm truncate flex-1">{name}</span>
              <span className="text-white font-medium ml-4">{count}</span>
            </div>
          ))
        ) : (
          <div className="text-gray-500 text-sm">No data yet</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4" />
              Back to Site
            </Link>
            <span className="text-white/20">|</span>
            <h1 className="font-bold text-white">Analytics Dashboard</h1>
          </div>
          <button 
            onClick={fetchAnalytics}
            disabled={loading}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {error ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
              <p className="text-red-400">{error}</p>
              <button 
                onClick={fetchAnalytics}
                className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Retry
              </button>
            </div>
          ) : loading && !analytics ? (
            <div className="text-center py-20">
              <RefreshCw className="w-8 h-8 text-gray-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Loading analytics...</p>
            </div>
          ) : analytics ? (
            <>
              {/* Last updated */}
              {lastUpdated && (
                <p className="text-xs text-gray-500 mb-6">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </p>
              )}

              {/* Overview Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <StatCard 
                  icon={Eye} 
                  label="Total Views" 
                  value={analytics.total_views.toLocaleString()} 
                />
                <StatCard 
                  icon={Users} 
                  label="Unique Visitors" 
                  value={analytics.unique_visitors.toLocaleString()} 
                />
                <StatCard 
                  icon={TrendingUp} 
                  label="Today" 
                  value={analytics.today_views.toLocaleString()} 
                  sublabel="page views"
                />
                <StatCard 
                  icon={Calendar} 
                  label="This Week" 
                  value={analytics.week_views.toLocaleString()} 
                  sublabel="page views"
                />
              </div>

              {/* Device breakdown */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <Monitor className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{analytics.by_device?.desktop || 0}</div>
                  <div className="text-xs text-gray-500">Desktop</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <Smartphone className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{analytics.by_device?.mobile || 0}</div>
                  <div className="text-xs text-gray-500">Mobile</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <Tablet className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{analytics.by_device?.tablet || 0}</div>
                  <div className="text-xs text-gray-500">Tablet</div>
                </div>
              </div>

              {/* Data tables */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <DataTable 
                  title="By Country" 
                  icon={Globe} 
                  data={analytics.by_country} 
                />
                <DataTable 
                  title="By City" 
                  icon={MapPin} 
                  data={analytics.by_city} 
                />
                <DataTable 
                  title="By Referrer" 
                  icon={LinkIcon} 
                  data={analytics.by_referrer} 
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <DataTable 
                  title="By Page" 
                  icon={Eye} 
                  data={analytics.by_page} 
                />
                <DataTable 
                  title="By Browser" 
                  icon={Monitor} 
                  data={Object.entries(analytics.by_browser || {})} 
                />
              </div>

              {/* Daily views chart */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-red-400" />
                  Daily Views (Last 30 Days)
                </h3>
                <div className="flex items-end gap-1 h-32">
                  {analytics.by_day && analytics.by_day.slice().reverse().map(([date, count], i) => {
                    const maxCount = Math.max(...analytics.by_day.map(d => d[1]));
                    const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                    return (
                      <div 
                        key={i} 
                        className="flex-1 bg-red-500/60 hover:bg-red-500 rounded-t transition-all cursor-pointer group relative"
                        style={{ height: `${Math.max(height, 4)}%` }}
                        title={`${date}: ${count} views`}
                      >
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-black border border-white/20 rounded px-2 py-1 text-xs opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                          {date}: {count}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent Visits */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4">Recent Visits</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-white/10">
                        <th className="pb-3 pr-4">Time</th>
                        <th className="pb-3 pr-4">Page</th>
                        <th className="pb-3 pr-4">Location</th>
                        <th className="pb-3 pr-4">Referrer</th>
                        <th className="pb-3">Device</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.recent_visits && analytics.recent_visits.slice(0, 20).map((visit, i) => (
                        <tr key={i} className="border-b border-white/5">
                          <td className="py-3 pr-4 text-gray-400">
                            {new Date(visit.timestamp).toLocaleString()}
                          </td>
                          <td className="py-3 pr-4 text-white">{visit.page_url}</td>
                          <td className="py-3 pr-4 text-gray-300">
                            {visit.city}, {visit.country}
                          </td>
                          <td className="py-3 pr-4 text-gray-400 truncate max-w-[150px]">
                            {visit.referrer}
                          </td>
                          <td className="py-3 text-gray-400 capitalize">{visit.device_type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
